import { useState, useEffect } from 'react';
import {
  ShieldCheckIcon,
  HeartIcon,
  SparklesIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  HomeIcon,
  DevicePhoneMobileIcon,
  AcademicCapIcon,
  BanknotesIcon,
  LightBulbIcon,
  ScaleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import Footer from '../components/common/Footer';
import Navigation from '../components/common/Navigation';
import MarketingHero from '../components/marketing/MarketingHero';
import MarketingCtaBand from '../components/marketing/MarketingCtaBand';

const timelineEvents = [
  {
    year: '1973',
    title: 'A vision of community finance',
    description:
      'Founded by local educators who believed in cooperative finance, Oakvale Credit Union began with a mission to provide fair, accessible services to teachers and their families.',
    icon: BuildingLibraryIcon,
  },
  {
    year: '1985',
    title: 'Expanding our family',
    description:
      'As our reputation for personalized service grew, we opened membership to more community members and introduced home loans and small business programs.',
    icon: HomeIcon,
  },
  {
    year: '1995',
    title: 'Pioneering digital access',
    description:
      'We embraced the digital shift early, offering online account access and tools that set the foundation for today’s mobile-first experience.',
    icon: SparklesIcon,
  },
  {
    year: '2008',
    title: 'Supporting through crisis',
    description:
      'During the financial crisis, we stood by our members. Our mortgage assistance program helped over a thousand families stay in their homes.',
    icon: ShieldCheckIcon,
  },
  {
    year: '2015',
    title: 'Banking at your fingertips',
    description:
      'Our mobile app brought instant deposits, alerts, and transfers—so members could manage money on their schedule without losing the personal touch.',
    icon: DevicePhoneMobileIcon,
  },
  {
    year: '2023',
    title: 'Embracing the future',
    description:
      'Celebrating our golden anniversary with over 100,000 members, we invested in smarter tools and advisory resources while keeping decisions local.',
    icon: ChartBarIcon,
  },
];

const testimonials = [
  {
    quote:
      'When I lost my job during the pandemic, Oakvale worked with me to defer my mortgage payments. They showed me that a credit union can still feel human.',
    author: 'Michael Rodriguez',
    role: 'Member since 2015',
  },
  {
    quote:
      'Oakvale helped me start my dream café. They are not just my credit union—they are partners in our success.',
    author: 'Sarah Chen',
    role: 'Local business owner',
  },
];

const AboutPage = () => {
  const [counters, setCounters] = useState({
    members: 0,
    assets: 0,
    satisfaction: 0,
    communityImpact: 0,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCounters((prev) => ({
        members: Math.min(prev.members + 1000, 100000),
        assets: Math.min(prev.assets + 50, 2500),
        satisfaction: Math.min(prev.satisfaction + 1, 98),
        communityImpact: Math.min(prev.communityImpact + 0.1, 5.0),
      }));
    }, 20);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <Navigation />

      <MarketingHero
        badge="Member-owned · Federally insured"
        title="Where financial strength"
        titleAccent="meets community values"
        description="For 50 years, Oakvale Credit Union has been more than a place to keep money—we are a cornerstone of community growth, innovation, and trusted partnership."
      >
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          <div className="rounded-2xl border border-white/10 bg-white/10 p-5 text-center backdrop-blur-sm transition hover:border-white/20">
            <div className="text-2xl font-bold text-white md:text-3xl">
              {counters.members.toLocaleString()}+
            </div>
            <div className="mt-1 text-xs text-stone-200/85 md:text-sm">Members</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/10 p-5 text-center backdrop-blur-sm transition hover:border-white/20">
            <div className="text-2xl font-bold text-white md:text-3xl">${counters.assets}M+</div>
            <div className="mt-1 text-xs text-stone-200/85 md:text-sm">In member assets</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/10 p-5 text-center backdrop-blur-sm transition hover:border-white/20">
            <div className="text-2xl font-bold text-white md:text-3xl">{counters.satisfaction}%</div>
            <div className="mt-1 text-xs text-stone-200/85 md:text-sm">Satisfaction</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/10 p-5 text-center backdrop-blur-sm transition hover:border-white/20">
            <div className="text-2xl font-bold text-white md:text-3xl">
              ${counters.communityImpact.toFixed(1)}M
            </div>
            <div className="mt-1 text-xs text-stone-200/85 md:text-sm">Community impact</div>
          </div>
        </div>
      </MarketingHero>

      <section className="border-b border-stone-200/80 bg-white py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl font-bold text-stone-900 md:text-4xl">Our mission</h2>
          <p className="mt-6 text-lg leading-relaxed text-stone-600 md:text-xl">
            To empower our members&apos; financial success through personalized service, practical solutions,
            and an unwavering commitment to community well-being. When our members thrive, our community
            prospers.
          </p>
        </div>
      </section>

      <section className="border-b border-stone-200/80 bg-gradient-to-b from-stone-100/90 to-stone-50 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 className="font-serif text-3xl font-bold text-stone-900 md:text-4xl">Our core values</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-stone-600">
              Principles that shape every conversation, product, and decision we make together with members.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              {
                Icon: ScaleIcon,
                title: 'Trust & integrity',
                body: 'We hold ourselves to strong security and ethical standards so your financial well-being stays protected.',
                ring: 'ring-emerald-200/80',
                iconBg: 'bg-emerald-100',
                iconColor: 'text-emerald-800',
              },
              {
                Icon: HeartIcon,
                title: 'Community focus',
                body: 'We invest in local growth, support small businesses, and show up where it matters for neighbors and members.',
                ring: 'ring-teal-200/80',
                iconBg: 'bg-teal-100',
                iconColor: 'text-teal-800',
              },
              {
                Icon: LightBulbIcon,
                title: 'Forward thinking',
                body: 'We adopt useful innovation without losing the personal relationships members expect from a credit union.',
                ring: 'ring-cyan-200/70',
                iconBg: 'bg-cyan-100',
                iconColor: 'text-cyan-800',
              },
            ].map(({ Icon, title, body, ring, iconBg, iconColor }) => (
              <div
                key={title}
                className={`rounded-3xl border border-stone-200 bg-white p-8 shadow-sm ring-1 ${ring} transition hover:shadow-md`}
              >
                <div
                  className={`mb-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl ${iconBg} transition group-hover:scale-105`}
                >
                  <Icon className={`h-6 w-6 ${iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-stone-900">{title}</h3>
                <p className="mt-3 leading-relaxed text-stone-600">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-stone-200/80 bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 className="font-serif text-3xl font-bold text-stone-900 md:text-4xl">The Oakvale difference</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-stone-600">
              Member-owned finance that goes beyond transactions to build lasting relationships.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                Icon: UserGroupIcon,
                title: 'Member-owned',
                body: 'As a not-for-profit cooperative, members are owners. Your success is our shared success.',
                iconBg: 'bg-emerald-100',
                iconColor: 'text-emerald-800',
              },
              {
                Icon: BanknotesIcon,
                title: 'Competitive value',
                body: 'Earnings go back to members through competitive loan rates and savings that reward loyalty.',
                iconBg: 'bg-teal-100',
                iconColor: 'text-teal-800',
              },
              {
                Icon: DevicePhoneMobileIcon,
                title: 'Digital + personal',
                body: 'Robust online and mobile tools with real people available when you want to talk something through.',
                iconBg: 'bg-sky-100',
                iconColor: 'text-sky-800',
              },
              {
                Icon: AcademicCapIcon,
                title: 'Financial education',
                body: 'Free resources and workshops so you can make confident decisions at every life stage.',
                iconBg: 'bg-amber-100',
                iconColor: 'text-amber-900',
              },
            ].map(({ Icon, title, body, iconBg, iconColor }) => (
              <div
                key={title}
                className="rounded-3xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-6 shadow-sm transition hover:shadow-md"
              >
                <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl ${iconBg}`}>
                  <Icon className={`h-6 w-6 ${iconColor}`} />
                </div>
                <h3 className="text-lg font-bold text-stone-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-600 md:text-base">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-stone-200/80 bg-stone-100/60 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 className="font-serif text-3xl font-bold text-stone-900 md:text-4xl">Our journey</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-stone-600">
              Five decades of growth, innovation, and commitment to the communities we serve.
            </p>
          </div>
          <div className="mx-auto max-w-3xl">
            {timelineEvents.map((event) => (
              <div key={event.year} className="group relative mb-12 flex items-start last:mb-0">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-emerald-200/80 bg-white shadow-sm ring-1 ring-emerald-100 transition group-hover:border-emerald-300 group-hover:shadow-md">
                  <event.icon className="h-7 w-7 text-emerald-800" />
                </div>
                <div className="ml-6 transition group-hover:translate-x-1 md:ml-8">
                  <div className="mb-2 flex flex-wrap items-baseline gap-3">
                    <span className="font-serif text-2xl font-bold text-emerald-800">{event.year}</span>
                    <h3 className="text-xl font-bold text-stone-900">{event.title}</h3>
                  </div>
                  <p className="leading-relaxed text-stone-600">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-stone-200/80 bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 className="font-serif text-3xl font-bold text-stone-900 md:text-4xl">Voices from our members</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-stone-600">
              Real stories from people who bank with neighbors, not distant shareholders.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {testimonials.map((t) => (
              <blockquote
                key={t.author}
                className="rounded-3xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-8 shadow-sm"
              >
                <StarIcon className="h-8 w-8 text-amber-400" aria-hidden />
                <p className="mt-4 text-lg leading-relaxed text-stone-700">&ldquo;{t.quote}&rdquo;</p>
                <footer className="mt-6 border-t border-stone-200/80 pt-6">
                  <p className="font-semibold text-stone-900">{t.author}</p>
                  <p className="text-sm text-stone-500">{t.role}</p>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-stone-200/80 bg-gradient-to-b from-emerald-50/40 to-stone-50 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 className="font-serif text-3xl font-bold text-stone-900 md:text-4xl">Making a difference</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-stone-600">
              Our commitment to community extends beyond everyday banking services.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              { stat: '$2M+', title: 'Education grants', copy: 'Scholarships and school partnerships that help students plan what is next.' },
              { stat: '10,000+', title: 'Volunteer hours', copy: 'Team members showing up for local service days and neighborhood projects.' },
              { stat: '$500K', title: 'Local business support', copy: 'Grants, lending, and mentorship that keep Main Street resilient.' },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm transition hover:shadow-md"
              >
                <div className="font-serif text-3xl font-bold text-emerald-800">{item.stat}</div>
                <h3 className="mt-2 text-xl font-bold text-stone-900">{item.title}</h3>
                <p className="mt-3 text-stone-600">{item.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarketingCtaBand
        heading="Join our financial family"
        body="Experience member-focused banking with local decisions and tools that fit real life."
        primaryLabel="Become a member"
        secondaryLabel="Contact us"
      />

      <Footer />
    </div>
  );
};

export default AboutPage;
